import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useLocation, useHistory } from "react-router-dom";

import { ImpactCategory } from "../../domain/impactCategories";

interface ImpactCategoryContextProps {
  impactCategory: ImpactCategory;
  setImpactCategory: (impactCategory: ImpactCategory) => void;
}

const ImpactCategoryContext = createContext<
  ImpactCategoryContextProps | undefined
>(undefined);

export function ImpactCategoryProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const history = useHistory();

  const params = new URLSearchParams(location.search);
  const categoryParam = params.get("category") as ImpactCategory | null;

  const isValidCategory = (value: any): value is ImpactCategory =>
    Object.values(ImpactCategory).includes(value);

  const [impactCategory, setImpactCategoryState] = useState<ImpactCategory>(
    categoryParam && isValidCategory(categoryParam)
      ? categoryParam
      : ImpactCategory.GHG
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set("category", impactCategory);
    history.replace({ search: params.toString() });
  }, [impactCategory, location.search, history]);

  const setImpactCategory = (newCategory: ImpactCategory) => {
    setImpactCategoryState(newCategory);
  };

  return (
    <ImpactCategoryContext.Provider
      value={{ impactCategory, setImpactCategory }}
    >
      {children}
    </ImpactCategoryContext.Provider>
  );
}

export function useImpactCategory() {
  const context = useContext(ImpactCategoryContext);
  if (!context) {
    throw new Error(
      "useImpactCategory must be used within an ImpactCategoryProvider"
    );
  }
  return context;
}
